import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import BN from 'bn.js'

const SOL_DECIMALS = Math.log10(LAMPORTS_PER_SOL)

export function solToLamports(amount: number): number {
  if (Number.isNaN(amount)) {
    return Number(0)
  }
  return new BN(Number(amount).toFixed(SOL_DECIMALS).replace('.', '')).toNumber()
}

export function formatPrice(price: number | string, minDigits = 0, maxDigits = 10) {
  const longPriceFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  })
  return longPriceFormatter.format(Number(price))
}

export function formatTime(time: number | string) {
  return Number(time) >= 10 ? time.toString() : `0${time}`
}

export function onlyNumber(e: any) {
  const keyCode = e.keyCode ?? e.which
  if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) {
    e.preventDefault()
  }
  if (keyCode === 46 && String(e.target.value).includes('.')) {
    e.preventDefault()
  }
}

export function lamportsToSol(lamports: number): number {
  if (typeof lamports === 'number') {
    return Math.abs(lamports) / LAMPORTS_PER_SOL
  }
  let signMultiplier = 1
  if (Number(lamports) < 0) {
    signMultiplier = -1
  }
  const absLamports = Math.abs(lamports)
  const lamportsString = absLamports.toString(10).padStart(10, '0')
  const splitIndex = lamportsString.length - SOL_DECIMALS
  const solString = `${lamportsString.slice(0, splitIndex)}.${lamportsString.slice(splitIndex)}`
  return signMultiplier * Number.parseFloat(solString)
}

const SI_SYMBOL = ['', 'K', 'M', 'G', 'T', 'P', 'E']

function abbreviateNumber(number: number, precision: number, trimZeros = false) {
  const tier = Math.trunc(Math.log10(number) / 3)
  let scaled = number
  const suffix = SI_SYMBOL[tier]
  if (tier > 0) {
    const scale = 10 ** (tier * 3)
    scaled = number / scale
  }

  if (Number.isNaN(scaled)) {
    return ''
  }
  const result = trimZeros ? Number(scaled.toFixed(precision)) : scaled.toFixed(precision)
  return result + (suffix ?? '')
}

export function formatAmount(val: number, precision = 5, abbr = true) {
  return abbr ? abbreviateNumber(val, precision) : val.toFixed(precision)
}

export function formatMoney(val: string | number, integer = false): string {
  if (val === undefined || val === null || val === '') {
    return ''
  }

  val = val.toString()

  const decimalSeparator = val.lastIndexOf('.')

  let integerPart = decimalSeparator === -1 ? val : val.slice(0, decimalSeparator)

  let fractionalPart = decimalSeparator === -1 ? null : val.slice(decimalSeparator + 1)

  // round integer value
  if (integer && fractionalPart && (+`0.${fractionalPart}` >= 0.5)) {
    integerPart = `${+integerPart + 1}`
  }

  if (fractionalPart) {
    fractionalPart = fractionalPart.slice(0, 2).replaceAll(/\D+/g, '')
    if (fractionalPart.length === 1) {
      fractionalPart += '0'
    }
  } else if (!fractionalPart) {
    fractionalPart = '00'
  }

  integerPart = integerPart.replaceAll(/\D+/g, '')
  if (!integerPart) {
    integerPart = '0'
  }

  let formatted = ''
  for (let i = 0; i < integerPart.length; i++) {
    formatted = i !== 0 && i % 3 === 0 ? `${integerPart[integerPart.length - i - 1]},${formatted}` : integerPart[integerPart.length - i - 1] + formatted
  }

  return integer ? formatted : `${formatted}.${fractionalPart}`
}

export function formatDateDMY(date: Date | string, separator = '/') {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date)).replaceAll('/', separator)
}

export const priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const longPriceFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 5,
})

export const feeFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 9,
})

export const formatPct = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export function formatAmountPrice(val: number | bigint) {
  return priceFormatter.format(val)
}

export function formatTimeToHMS(time: number) {
  const _h = time / 1000 / 60 / 60
  const h = Math.floor(_h)
  const m = Math.floor((_h - h) * 60)
  const s = Math.ceil(((_h - h) * 60 - m) * 60)
  return `${h}h ${formatTime(m)}m ${formatTime(s)}s`
}

export function getSolPriceFormatter(n = 5) {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: n,
    maximumFractionDigits: n,
  })
}

export function solFormatter(val: number, text = ' SOL') {
  if (val === 0) {
    return `0${text}`
  }
  const decimals = Math.max(13 - `${val}`.length, 5)
  const solPriceFormatter = getSolPriceFormatter(decimals)
  return `${val < 0 ? '-' : ''}${solPriceFormatter.format(lamportsToSol(val))}${text}`
}

export function formatAndTrimAmount(val: number, precision = 5, abbr = true) {
  return abbr ? abbreviateNumber(val, precision, true) : Number(val.toFixed(precision))
}

export function formatWithPrecision(val: number, precision = 10) {
  const value = Number(val).toFixed(precision)
  const rewardParts = value.split('.')
  if (rewardParts[1] && rewardParts[0] && rewardParts[0] === '0' && val !== 0) {
    let zeroCount = 0
    for (let i = 0; i < rewardParts[1].length; i++) {
      if (rewardParts[1][i] === '0') {
        zeroCount++
      } else {
        break
      }
    }
    rewardParts[1] = rewardParts[1].slice(0, zeroCount + 3)
    return `${rewardParts[0]}.${rewardParts[1]}`
  }
  return formatAmount(val, 3)
}
